import React from 'react';

const sections = [
  {
    heading: '1. Introduction',
    body: [
      `This Privacy Policy explains what personal information O!ng Lab collects, how we use it, and what choices you have.`,
      `We wrote it in plain English so it is easy to understand.`,
    ],
  },
  {
    heading: '2. Who we are',
    body: [
      `O!ng Lab provides Korean language, culture, career, and settlement support services for global users.`,
      `· Website: www.oinglab-korean.com`,
      `· Email: contact@oinglab-korean.com`,
      `· Privacy officer (개인정보 보호책임자): Hayeon Kwon (권하연)`,
      `· Business name: 오잉랩 (O!ng Lab), registration number 179-45-01005`,
      `· Business address: 경기도 수원시 광교호수공원로 277번길`,
    ],
  },
  {
    heading: '3. Information we collect',
    body: [
      `We collect information in three ways:`,
      `· Information you give us directly (Section 4)`,
      `· Information processed during payment (Section 5)`,
      `· Information collected automatically when you use the site (Section 6)`,
    ],
  },
  {
    heading: '4. Information you provide directly',
    body: [
      `When you sign up, we collect:`,
      `· Email address (required)`,
      `· Name (required)`,
      `· Country and city where you live (required)`,
      `· Date of birth (required)`,
      `· Gender (optional — you can choose “Prefer not to say”)`,
      ``,
      `Depending on how you use the service, we may also ask for:`,
      `· Phone number`,
      `· Payment information (see Section 5)`,
      `· Work or study experience (for career features)`,
      `· Information about your learning, career, or workshop participation`,
      `· Information needed to answer your questions or support requests`,
      `We only ask for this information when it is needed.`,
      ``,
      `Pathfinder — location and school information`,
      `When you use Pathfinder, we may also collect:`,
      `· Your current country and city of residence (to assess visa eligibility and settlement pathways in Korea)`,
      `· Your school name and school location (to determine whether your school is recognized in Korea, which is used to identify available visa paths)`,
      `This information is entered by you directly. We do not use GPS, device location APIs, or any automatic location tracking.`,
    ],
  },
  {
    heading: '5. Information collected during payment',
    body: [
      `When you buy a paid product, payment is handled by payment providers such as Toss Payments and PayPal.`,
      `O!ng does not store your card number on our servers.`,
      ``,
      `During payment and refunds, we may process:`,
      `· Payment method and currency (KRW or USD)`,
      `· Payment amount, status, and date`,
      `· Refund status, approval, and processing dates`,
      `· Payment approval number or transaction ID (including PayPal transaction ID, if you pay with PayPal)`,
      `· The product you purchased`,
      `· Booking status for workshops and live sessions (for example: confirmed, cancelled, refunded)`,
    ],
  },
  {
    heading: '6. Information collected automatically',
    body: [
      `When you visit our site, we may automatically collect:`,
      `· Device and browser type`,
      `· Pages you visit and how you move through the site`,
      `· Approximate region (not your exact location)`,
      `· Log data such as access time and errors`,
      `This is collected through cookies and analytics tools (see Sections 7 and 13).`,
    ],
  },
  {
    heading: '7. Cookies and similar technologies',
    body: [
      `We use cookies to make the service work and to understand how people use it.`,
      `· Some cookies are needed for basic functions, like staying logged in.`,
      `· Some cookies help us analyze and improve the service (Google Analytics 4 and Microsoft Clarity — see Section 13).`,
      `You can control cookies in your browser settings.`,
      `Blocking some cookies may affect how the site works.`,
    ],
  },
  {
    heading: '8. How we use personal information',
    body: [
      `We use your information to:`,
      `· Provide the service and manage your account`,
      `· Process bookings, payments, and refunds`,
      `· Send service emails, such as booking, waitlist, and payment notifications (see Section 12)`,
      `· Answer your questions and support requests`,
      `· Improve the service and fix problems`,
      `· Prevent fraud and misuse`,
      `· Meet legal requirements`,
    ],
  },
  {
    heading: '9. Why we are allowed to use your information',
    body: [
      `We process your information:`,
      `· To provide the service you signed up for`,
      `· With your consent, where required`,
      `· To meet legal obligations (for example, keeping payment records)`,
      `· To improve and protect the service`,
    ],
  },
  {
    heading: '10. Sharing and outsourcing',
    body: [
      `We do not sell your personal information.`,
      `We share information only when needed, with:`,
      `· Payment providers — Toss Payments and PayPal, to process payments and refunds (Section 11)`,
      `· Cloud hosting — Amazon Web Services (AWS), to run our servers and store data securely`,
      `· Analytics — Google (Google Analytics 4) and Microsoft (Clarity), to understand and improve how the service is used (Section 13)`,
      `· Online sessions — Google Meet, to run the live sessions you join`,
      `· Authorities, if the law requires it`,
      `Service emails are sent from our own servers. We do not use an external email provider.`,
    ],
  },
  {
    heading:
      '11. Payment processing through Toss Payments, PayPal, and related providers',
    body: [
      `· Payments are processed by Toss Payments and, for some payment methods, PayPal.`,
      `· Korean cards are processed in KRW. Overseas cards and PayPal are processed in USD.`,
      `· These providers process your payment details under their own privacy policies.`,
      `· O!ng receives payment results (such as status and transaction ID), not your full card number.`,
    ],
  },
  {
    heading: '12. Email and service notifications',
    body: [
      `We send service emails such as:`,
      `· Booking and purchase confirmations`,
      `· Waitlist offers (when a seat opens for you)`,
      `· Payment and refund updates`,
      `· Workshop schedule information`,
      `· Important service notices`,
      `· Replies to your questions`,
      `These are part of the service, so they are sent to all users.`,
      `We send these emails from our own servers.`,
    ],
  },
  {
    heading: '13. Analytics tools: Google Analytics 4 and Microsoft Clarity',
    body: [
      `We plan to use Google Analytics 4 (GA4) and Microsoft Clarity to:`,
      `· Understand visitor numbers and how pages are used`,
      `· See where users have problems or leave`,
      `· Improve usability and fix errors`,
      `· Understand payment conversion`,
      `These tools use cookies and may process data on servers outside Korea.`,
      `Analytics data is used in a form that does not directly identify you.`,
    ],
  },
  {
    heading: '14. International users and cross-border processing',
    body: [
      `O!ng Lab is based in the Republic of Korea, and our users live around the world.`,
      `· Your information is processed in Korea.`,
      `· Our servers (AWS) are located in Korea (Seoul region).`,
      `· Some service providers may process data in other countries: Google (Analytics, Meet), Microsoft (Clarity), and PayPal.`,
    ],
  },
  {
    heading: '15. How long we keep your information',
    body: [
      `· Account information: deleted when you delete your account, unless the law requires us to keep it for a set period.`,
      `· Payment and transaction records: kept as required by law and payment provider, card company, and tax rules.`,
      `· Support and inquiry records: may be kept for a set period to handle follow-up questions and disputes.`,
      `· Analytics data: processed in a form that does not directly identify you.`,
    ],
  },
  {
    heading: '16. Account deletion and data deletion',
    body: [
      `When you delete your account, we delete your personal information.`,
      `Some records may be kept for a limited period:`,
      `· Payment and refund records`,
      `· Records the law requires us to keep`,
      `· Records needed to handle an ongoing dispute`,
      `· Minimal information needed to prevent fraud or repeated misuse`,
      `· Support history`,
      `To delete your account, go to Settings > Delete account.`,
      `You can also email us at contact@oinglab-korean.com.`,
    ],
  },
  {
    heading: '17. Your rights',
    body: [
      `You can:`,
      `· See the personal information we have about you`,
      `· Ask us to correct it`,
      `· Ask us to delete it (see Section 16)`,
      `· Withdraw consent you gave earlier`,
      `To use these rights, email contact@oinglab-korean.com.`,
      `We will respond within the time required by law.`,
    ],
  },
  {
    heading: '18. Security',
    body: [
      `We work to protect your information with reasonable security measures, such as access controls and encryption where appropriate.`,
      `No online service can be 100% secure, but we take protection seriously and review our measures regularly.`,
    ],
  },
  {
    heading: '19. Children and minors',
    body: [
      `O!ng is for users 18 years or older.`,
      `We do not knowingly collect personal information from anyone under 18. If you believe a minor gave us personal information, contact us and we will delete it.`,
    ],
  },
  {
    heading: '20. Changes to this Privacy Policy',
    body: [
      `We may update this policy.`,
      `· The “Last updated” date shows the latest version.`,
      `· For important changes, we will notify you by email or site notice before the change takes effect.`,
    ],
  },
  {
    heading: '21. Contact',
    body: [
      `Questions about privacy?`,
      `Email: contact@oinglab-korean.com`,
      `Privacy officer: Hayeon Kwon (권하연)`,
    ],
  },
];

/** 정책 문서 한 버전. PolicyDocument가 레지스트리를 통해 읽어 렌더링한다. */
export default {
  title: 'Privacy Policy',
  lastUpdated: 'June 11, 2026',
  effectiveDate: '2026-06-11',
  sections,
};
